import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter, Percent, Search, SlidersHorizontal } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import {
  Button,
  Input,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Badge,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
} from '@vnetwork/ui/primitives';
import {
  DataTable,
  EmptyState,
  FilterBuilder,
  Pagination,
  StatusPill,
  type ColumnDef,
  type FilterCondition,
  type FilterField,
} from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { Card, CardBody, Skeleton } from '@vnetwork/ui/primitives';
import { usePromos, pgTimestampToIso, type Promo } from '@/api/promos';
import { paginationView } from '@/pages/customers/pagination-summary';
import { TableLink } from '@/components/table-link';
import { formatDate, formatMoney } from '@/lib/format';

const PAGE_SIZE = 20;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/** Map PromoStatus to StatusPill tone. */
function statusTone(status: string): 'neutral' | 'success' | 'danger' {
  if (status === 'active') return 'success';
  if (status === 'expired') return 'danger';
  return 'neutral';
}

function humanizeStatus(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function formatDiscount(promo: Promo): string {
  if (promo.promo_type === 'percentage') return `${promo.discount_value}%`;
  if (promo.promo_type === 'fixed_amount') return formatMoney(promo.discount_value);
  return String(promo.discount_value);
}

function formatValidity(promo: Promo): string {
  const until = pgTimestampToIso(promo.valid_until);
  if (!until) return 'No expiry';
  return formatDate(until);
}

// Backend GetPromos only filters by search/status/referrer_id (db/queries/promos.sql).
// promo_type has NO server-side WHERE clause, so a Type filter here would silently
// drop on pagination — omitted rather than shown as a non-functional control.
const FILTER_FIELDS: FilterField[] = [
  {
    value: 'status',
    label: 'Status',
    input: 'select',
    options: [
      { label: 'Active', value: 'active' },
      { label: 'Inactive', value: 'inactive' },
      { label: 'Expired', value: 'expired' },
    ],
  },
];

/** colId — stable toggle key for column visibility. */
function colId<Row>(c: ColumnDef<Row, unknown>): string {
  return typeof c.id === 'string' ? c.id : String(c.header ?? '');
}

const COLUMNS: ColumnDef<Promo, unknown>[] = [
  {
    id: 'code',
    header: 'Code',
    cell: ({ row }) => (
      <TableLink to={`/promos/${row.original.promo_id}`}>
        <code
          style={{
            fontFamily: 'monospace',
            fontWeight: 'var(--vn-font-weight-semibold)',
            fontSize: 'var(--vn-font-size-sm)',
          }}
        >
          {row.original.code}
        </code>
      </TableLink>
    ),
  },
  {
    id: 'name',
    header: 'Name',
    cell: ({ row }) => (
      <span
        style={{
          fontSize: 'var(--vn-font-size-sm)',
          overflowWrap: 'anywhere',
        }}
      >
        {row.original.name}
      </span>
    ),
  },
  {
    id: 'promo_type',
    header: 'Type',
    cell: ({ row }) => {
      const type = row.original.promo_type;
      const label =
        type === 'percentage' ? 'Percentage'
        : type === 'fixed_amount' ? 'Fixed amount'
        : type === 'credit' ? 'Credit'
        : type;
      return <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>{label}</span>;
    },
  },
  {
    id: 'discount_value',
    header: 'Discount',
    cell: ({ row }) => (
      <span
        style={{
          fontSize: 'var(--vn-font-size-sm)',
          fontWeight: 'var(--vn-font-weight-semibold)',
        }}
      >
        {formatDiscount(row.original)}
      </span>
    ),
  },
  {
    id: 'current_uses',
    header: 'Uses',
    cell: ({ row }) => {
      const { current_uses, max_uses } = row.original;
      return (
        <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>
          {current_uses}
          {max_uses != null ? ` / ${max_uses}` : ''}
        </span>
      );
    },
  },
  {
    id: 'valid_until',
    header: 'Valid until',
    cell: ({ row }) => (
      <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>
        {formatValidity(row.original)}
      </span>
    ),
  },
  {
    id: 'referrer_name',
    header: 'Affiliate',
    cell: ({ row }) => (
      <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>
        {row.original.referrer_name || 'System'}
      </span>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <StatusPill tone={statusTone(row.original.status)}>
        {humanizeStatus(row.original.status)}
      </StatusPill>
    ),
  },
  {
    id: 'created_at',
    header: 'Created',
    cell: ({ row }) => (
      <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>
        {formatDate(pgTimestampToIso(row.original.created_at) ?? undefined)}
      </span>
    ),
  },
];

/**
 * `/promos` — Promos list page: server-paginated table with search + FilterBuilder.
 * Uses limit/offset (the backend does not accept page/page_size).
 */
export default function PromosPage() {
  const navigate = useNavigate();

  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState(1);
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 400);
  const [filterOpen, setFilterOpen] = useState(false);
  const [conditions, setConditions] = useState<FilterCondition[]>([]);
  const [visible, setVisible] = useState<Record<string, boolean>>(
    () => Object.fromEntries(COLUMNS.map((c) => [colId(c), true])),
  );

  // Derive status filter from applied conditions
  const statusFilter = conditions.find((c) => c.field === 'status')?.value;

  const { data, isLoading, isError, isFetching, refetch } = usePromos({
    search: query || undefined,
    status: statusFilter || undefined,
    limit: PAGE_SIZE,
    offset,
  });

  const rows = data?.results ?? [];
  const pagination = data?.pagination;
  const pageCount = pagination?.total_pages ?? 1;
  const total = pagination?.total ?? rows.length;

  const visibleColumns = useMemo(
    () => COLUMNS.filter((c) => visible[colId(c)] !== false),
    [visible],
  );

  const appliedFieldCount = new Set(conditions.map((c) => c.field)).size;

  const onSearch = (value: string) => {
    setRawQuery(value);
    setOffset(0);
    setPage(1);
  };

  const applyFilters = (next: FilterCondition[]) => {
    setConditions(next.filter((c) => c.value !== ''));
    setOffset(0);
    setPage(1);
    setFilterOpen(false);
  };

  const onPageChange = (p: number) => {
    setPage(p);
    setOffset((p - 1) * PAGE_SIZE);
  };

  return (
    <Container size="full">
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <PageHeader
          title="Promos"
          description="Discount codes and promotional campaigns"
          actions={
            <Button
              variant="primary"
              startIcon={<Percent size={15} />}
              onClick={() => navigate('/promos/new')}
            >
              New promo
            </Button>
          }
        />

        {/* Toolbar */}
        <Toolbar
          style={{ marginBottom: 0 }}
          start={
            <Input
              value={rawQuery}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="Search by code or name…"
              startSlot={<Search size={15} />}
              style={{ maxWidth: 320 }}
            />
          }
          end={
            <>
              <Popover open={filterOpen} onOpenChange={setFilterOpen}>
                <PopoverTrigger asChild>
                  <Button variant="secondary" startIcon={<Filter size={15} />}>
                    Filters{' '}
                    {appliedFieldCount ? (
                      <Badge tone="primary">{appliedFieldCount}</Badge>
                    ) : null}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" style={{ padding: 0 }}>
                  <FilterBuilder
                    fields={FILTER_FIELDS}
                    defaultValue={conditions.length ? conditions : undefined}
                    onApply={applyFilters}
                    onClose={() => setFilterOpen(false)}
                  />
                </PopoverContent>
              </Popover>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="secondary"
                    startIcon={<SlidersHorizontal size={15} />}
                  >
                    Display options
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
                  {COLUMNS.map((c) => {
                    const id = colId(c);
                    return (
                      <DropdownMenuCheckboxItem
                        key={id}
                        checked={visible[id] !== false}
                        onCheckedChange={(v) =>
                          setVisible((prev) => ({ ...prev, [id]: v === true }))
                        }
                        onSelect={(e) => e.preventDefault()}
                      >
                        {typeof c.header === 'string' ? c.header : id}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          }
        />

        {/* Table */}
        {isError ? (
          <Alert
            tone="danger"
            title="Failed to load promos"
            actions={
              <Button size="sm" variant="ghost" onClick={() => refetch()}>
                Retry
              </Button>
            }
          />
        ) : isLoading ? (
          <Card flat>
            <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} style={{ height: 24 }} />
              ))}
            </CardBody>
          </Card>
        ) : (
          <DataTable
            columns={visibleColumns}
            data={rows}
            getRowId={(row) => String(row.promo_id)}
            onRowClick={(row) => navigate(`/promos/${row.promo_id}`)}
            emptyState={
              <EmptyState
                icon={<Percent size={24} />}
                title="No promos found"
                description="Try adjusting your search or filters, or create a new promo."
              />
            }
            footer={(() => {
              const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);
              return (
                <Pagination
                  page={page}
                  pageCount={view.pageCount}
                  onPageChange={onPageChange}
                  summary={`${view.summary}${isFetching ? ' …' : ''}`}
                />
              );
            })()}
          />
        )}
      </div>
    </Container>
  );
}
